import DataLabelRow from '@lyra/core/components/DataLabelRow'
import Section from '@lyra/core/components/Section'
import formatUSD from '@lyra/core/utils/formatUSD'
import { Ticker } from '@lyra/web/constants/instruments'
import { OrderQuote, OrderQuoteParams } from '@lyra/web/constants/order'
import { collateralConfig, CollateralId } from '@lyra/web/constants/tokens'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { formatCollateralName } from '@lyra/web/utils/markets'
import { formatCollateralBalance } from '@lyra/web/utils/tokens'
import { coerce } from '@lyra/web/utils/types'

type Props = {
  ticker: Ticker
  quoteParams: OrderQuoteParams
  quote?: OrderQuote | null
  isLoading?: boolean
}

const TradeFormSpotTicket = ({ ticker, quoteParams, quote, isLoading }: Props) => {
  const isBuy = quoteParams.direction === 'buy'

  const amount = +quoteParams.amount
  const quoteAmount = amount * +quoteParams.limit_price
  // Fee is accounted for in quote
  const marginRequired = quote ? +quote.pre_initial_margin - +quote.post_initial_margin : 0

  const { buyingPower, subaccountData } = useSubaccount()

  const quoteCollateralId = coerce(CollateralId, ticker.quote_currency)
  const quoteBalance =
    quoteCollateralId && subaccountData
      ? subaccountData.collateralBalances[quoteCollateralId]?.balance ?? BigInt(0)
      : 0
  const baseCollateralId = coerce(CollateralId, ticker.base_currency)
  const baseBalance =
    subaccountData && baseCollateralId
      ? subaccountData?.collateralBalances[baseCollateralId]?.balance
      : 0

  const isBorrowing = subaccountData && isBuy && quoteAmount > quoteBalance

  return (
    <Section.YStack>
      <DataLabelRow
        label={marginRequired >= 0 ? 'Margin Required' : 'Margin Unlocked'}
        value={isLoading ? '...' : formatUSD(Math.abs(marginRequired))}
      />
      <DataLabelRow
        label="Buying Power"
        color={quote && !quote.is_valid ? 'red' : 'primary'}
        value={formatUSD(buyingPower)}
      />
      <DataLabelRow
        label={isBuy ? (isBorrowing ? 'Borrowing' : 'Pay With') : 'Receive'}
        value={quoteCollateralId}
      />
      {quoteCollateralId ? (
        <DataLabelRow
          label={`${formatCollateralName(quoteCollateralId)} Balance`}
          color={quote && !quote.is_valid ? 'red' : 'primary'}
          value={
            collateralConfig[quoteCollateralId].isDollarFormat
              ? formatUSD(quoteBalance)
              : formatCollateralBalance(quoteBalance, quoteCollateralId)
          }
        />
      ) : null}
      {baseCollateralId ? (
        <DataLabelRow
          label={`${formatCollateralName(baseCollateralId)} Balance`}
          color={quote && !quote.is_valid ? 'red' : 'primary'}
          value={
            collateralConfig[baseCollateralId].isDollarFormat
              ? formatUSD(baseBalance)
              : formatCollateralBalance(baseBalance, baseCollateralId)
          }
        />
      ) : null}
    </Section.YStack>
  )
}

export default TradeFormSpotTicket
